import { useDispatch } from 'react-redux';
import { AttentionTriangleIcon } from '@rsos/assets/icons';
import { Button } from '@rsos/base-components';
import { useDiscrepancySettings } from '@rsos/capstone/discrepancy-reporting/hooks';
import { DISCREPANCY_REPORTING } from '@rsos/capstone/src/constants/capabilities';
import { DISCREPANCY_REPORTING_PERMISSION } from '@rsos/constants/permissions';
import { EmergencyCallSecondaryLocation } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCalls.types';
import { setSelectedTool } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import { setSelectedTab } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import {
  ALI_DISCREPANCY_TOOL,
  TOOLS_TAB,
} from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import ConditionalFeatureDisplay from '../Common/ConditionalFeatureDisplay/ConditionalFeatureDisplay';
import { isSourceAniAli } from '../MostRecentLocationCard/helpers';
import { AliDiscrepancyToolProperties } from './AliDiscrepancyForm/AliDiscrepancyForm';

interface ReportAliDiscrepancyButtonProps {
  location: EmergencyCallSecondaryLocation;
  phoneNumber?: string;
}

export function ReportAliDiscrepancyButton({
  location,
  phoneNumber,
}: ReportAliDiscrepancyButtonProps) {
  const dispatch = useDispatch();
  const isAniAli = isSourceAniAli(location);
  const { settings, loading: settingsLoading } = useDiscrepancySettings();

  // Check if INdigital discrepancy settings are configured
  const isConfigured =
    !settingsLoading &&
    settings.targetState &&
    settings.clientID &&
    settings.clientSecret;
  const handleReportDiscrepancy = () => {
    // Extract address data from location object
    const originalAddress = location.address_single_line || location.rgAddress;
    const classOfService = location.class_of_service;
    // NOTE inesn is only available for ANI/ALI locations from in_digital (see
    // ali_ingress service's handler_ali_indigital.py and XmlOutSchema). When we
    // get ALI data (as EIDO) from GMR there is no ESN as of 2026-02-06.
    const esn = location.inesn;
    const community = location.city;
    // Raw ALI spill for roundtrip to discrepancy API
    const rawAli = location.raw_ali;

    // Prepare tool properties with available address data
    const toolProperties: AliDiscrepancyToolProperties = {
      originalAddress,
      classOfService,
      phoneNumber,
      esn,
      community,
      rawAli,
    };

    // Switch to Tools tab with ALI Discrepancy form displayed
    dispatch(
      setSelectedTool({
        tool: ALI_DISCREPANCY_TOOL,
        toolProperties,
      }),
    );
    dispatch(setSelectedTab(TOOLS_TAB));
  };

  return (
    <ConditionalFeatureDisplay
      dataName="report-ali-discrepancy-button"
      conditions={[
        { showIf: isAniAli, reason: 'not-ani-ali' },
        { capabilityEnabled: DISCREPANCY_REPORTING },
        { permissionEnabled: DISCREPANCY_REPORTING_PERMISSION },
        { showIf: !settingsLoading, reason: 'settings-loading' },
        { showIf: !!isConfigured, reason: 'settings-not-configured' },
      ]}
    >
      <Button
        dataName="report-ali-discrepancy-button"
        onClick={handleReportDiscrepancy}
        btnType="tertiary"
        size="xxsmall"
      >
        <AttentionTriangleIcon width={12} height={12} />
      </Button>
    </ConditionalFeatureDisplay>
  );
}
